import React, { Component } from 'react';
import { ProductStatus } from '~/Api/Product';
import Icon from '~/UI/Icon/Icon';
import Tooltip from '~/UI/Tooltip';

import styles from './styles.less';

interface Props {
	status: ProductStatus;
	historicData?: boolean;
	referenceDataSource?: boolean;
}

const IconNames = [null, 'green-clock', 'yellow-question-mark', 'orange-clock', 'red-exclamation-mark'];

export default class StatusIcon extends Component<Props> {
	render() {
		const statusSeverity = this.resolveStatusSeverity();
		const complianceSeverity = this.resolveComplianceSeverity();
		const iconName = IconNames[Math.max(statusSeverity, complianceSeverity)] ?? null;
		if (iconName === null) {
			return null;
		}

		const icon = this.renderIcon(iconName);
		const statusText = this.renderStatusText();
		const complianceText = this.renderComplianceText();

		return (
			<Tooltip position='left' reference={icon}>
				{statusText !== null ? (
					<div>
						{this.renderIcon(IconNames[statusSeverity] ?? 'red-exclamation-mark')} {statusText}
					</div>
				) : null}
				{complianceText !== null ? (
					<div>
						{this.renderIcon(IconNames[complianceSeverity] ?? 'red-exclamation-mark')} {complianceText}
					</div>
				) : null}
			</Tooltip>
		);
	}

	private renderIcon(iconName: string) {
		return (
			<span className={styles.container}>
				<Icon name={iconName} />
			</span>
		);
	}

	private resolveStatusSeverity(): number {
		const { status, historicData } = this.props;

		if (historicData === undefined && (status.isRemoved() || status.isBeforeIntroduction())) {
			return 4;
		}
		if (status.isRemoved() && historicData === true) {
			return 4;
		}
		if (status.isRemoved() && historicData === false) {
			return 4;
		}
		if (status.isBeforeIntroduction()) {
			return 4;
		}
		if (status.isDanger()) {
			return 4;
		}
		if (status.isWarning()) {
			return 2;
		}
		if (status.isBeforeValidity()) {
			return 1;
		}
		if (status.isAfterValidity()) {
			return 3;
		}
		return 0;
	}

	private renderStatusText(): string | null {
		const { status, historicData } = this.props;

		if (historicData === undefined && (status.isRemoved() || status.isBeforeIntroduction())) {
			return 'Product is no longer present in current dataset.';
		}
		if (status.isRemoved() && historicData === true) {
			return 'Product is no longer present in the current dataset on the specified date.';
		}
		if (status.isRemoved() && historicData === false) {
			return 'Product is no longer present in current dataset.';
		}
		if (status.isBeforeIntroduction()) {
			return 'Product was not yet present in the current dataset on the specified date';
		}
		if (status.isDanger()) {
			return "This product shouldn't be used for referencing. Please check product details.";
		}
		if (status.isWarning()) {
			return 'This product might not be suitable for referencing. Please make sure this product can be used for your purpose.';
		}
		if (status.isBeforeValidity()) {
			return 'Product has validity that begins in the future and might not be suitable for referencing yet.';
		}
		if (status.isAfterValidity()) {
			return "Product validity has ended and it shouldn't be used for referencing. Please check product details.";
		}
		return null;
	}

	private resolveComplianceSeverity(): number {
		return this.props.referenceDataSource === false ? 4 : 0;
	}

	private renderComplianceText(): string | null {
		return this.props.referenceDataSource === false
			? 'DataSource of this product is not officially recognized and might not be suitable for price referencing.'
			: null;
	}
}
